import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

/**
 * Admin data access for provider credentials and their allocations.
 *
 * THE BOUNDARY THIS RESPECTS (Steph's option A, 2026-08-03)
 * ---------------------------------------------------------
 * PLATFORM ADMIN ONLY. Not workspace admins -- billing is not delegated to
 * users, "even admins who could bill other workspaces". Enforced at the API
 * router, not here: this hook simply has no route available to a non-admin.
 *
 * THERE IS NO WAY TO READ A KEY BACK, AND THAT IS NOT A GAP
 * --------------------------------------------------------
 * Note what is absent below: any function that fetches key material. Not
 * masked, not partial, not "reveal once". The API serves no such thing --
 * services/admin_credential_service.py never decrypts, asserted by AST
 * inspection in the test suite. `last_4` is the whole read affordance: show
 * WHICH key is configured, never WHAT it is.
 *
 * So the UI cannot display a key even by accident. The boundary lives in the
 * service, not in this file's restraint. If a future screen wants to show a
 * key, that is a backend conversation first -- and the answer is no.
 *
 * A key travels in exactly one direction: into `createCredential` or
 * `rotateCredential`, once, and never comes back.
 */
export const useAdminCredentials = () => {
    const [credentials, setCredentials] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    const reportError = useCallback((title, error) => {
        console.error(title, error);
        toast({
            title,
            description: error?.message || 'Unexpected error',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    }, [toast]);

    const detailFrom = async (response, fallback) => {
        try {
            const data = await response.json();
            return data.detail || fallback;
        } catch (e) {
            return fallback;
        }
    };

    const fetchCredentials = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth('/api/admin/credentials');
            if (!response.ok) {
                throw new Error(await detailFrom(response, 'Failed to load credentials'));
            }
            const data = await response.json();
            setCredentials(data);
            return data;
        } catch (error) {
            reportError('Could not load credentials', error);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [reportError]);

    const createCredential = useCallback(async ({ provider, name, apiKey }) => {
        const response = await fetchWithAuth('/api/admin/credentials', {
            method: 'POST',
            body: { provider, name, api_key: apiKey },
        });
        if (!response.ok) {
            // 503 here means CREDENTIAL_ENCRYPTION_KEY is not set on the
            // instance. Surfaced verbatim because the server's message says
            // exactly what to do; a generic error would send someone hunting.
            throw new Error(await detailFrom(response, 'Failed to save credential'));
        }
        const created = await response.json();
        await fetchCredentials();
        toast({
            title: 'Credential saved',
            // Says the quiet part out loud, twice over: it is encrypted, and it
            // is not in use until allocated. Creation grants nothing, exactly
            // like creating a workspace grants no membership.
            description: `${created.name} (…${created.last_4}) — encrypted, not yet allocated to any workspace`,
            status: 'success',
            duration: 5000,
            isClosable: true,
        });
        return created;
    }, [fetchCredentials, toast]);

    /**
     * Replace the key material in place.
     *
     * Allocations are deliberately untouched: a shared key is rotated ONCE
     * rather than re-entered for every workspace using it. That is the whole
     * operational point of separating identity from allocation.
     */
    const rotateCredential = useCallback(async (credentialId, apiKey) => {
        const response = await fetchWithAuth(`/api/admin/credentials/${credentialId}`, {
            method: 'PATCH',
            body: { api_key: apiKey },
        });
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to rotate credential'));
        }
        const updated = await response.json();
        await fetchCredentials();
        toast({
            title: 'Key replaced',
            description: updated.allocation_count > 0
                ? `${updated.name} (…${updated.last_4}) — ${updated.allocation_count} workspace(s) now use the new key`
                : `${updated.name} (…${updated.last_4})`,
            status: 'success',
            duration: 5000,
            isClosable: true,
        });
        return updated;
    }, [fetchCredentials, toast]);

    const renameCredential = useCallback(async (credentialId, name) => {
        const response = await fetchWithAuth(`/api/admin/credentials/${credentialId}`, {
            method: 'PATCH',
            body: { name },
        });
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to rename credential'));
        }
        const updated = await response.json();
        await fetchCredentials();
        return updated;
    }, [fetchCredentials]);

    const deleteCredential = useCallback(async (credentialId) => {
        const response = await fetchWithAuth(`/api/admin/credentials/${credentialId}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            // 409 is the in-use rule, and the server names the affected
            // workspaces. Passed through verbatim: that list IS the blast
            // radius, and it is the only thing that makes the refusal
            // actionable.
            throw new Error(await detailFrom(response, 'Failed to delete credential'));
        }
        await fetchCredentials();
    }, [fetchCredentials]);

    const allocateCredential = useCallback(async (credentialId, workspaceId) => {
        const response = await fetchWithAuth('/api/admin/credentials/allocations', {
            method: 'POST',
            body: { credential_id: credentialId, workspace_id: workspaceId },
        });
        if (!response.ok) {
            // 409 means that workspace already has a key for this provider.
            // One per provider per workspace keeps resolution unambiguous, and
            // the server explains how to swap.
            throw new Error(await detailFrom(response, 'Failed to allocate credential'));
        }
        const updated = await response.json();
        await fetchCredentials();
        return updated;
    }, [fetchCredentials]);

    const deallocateCredential = useCallback(async (credentialId, workspaceId) => {
        const response = await fetchWithAuth(
            `/api/admin/credentials/${credentialId}/allocations/${workspaceId}`,
            { method: 'DELETE' }
        );
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to remove allocation'));
        }
        const updated = await response.json();
        await fetchCredentials();
        toast({
            title: 'Allocation removed',
            // Important and easy to miss: the workspace does not lose access to
            // the provider, it falls back to the instance-wide env key. Saying
            // so prevents "why is it still working?" confusion.
            description: 'That workspace now falls back to the instance-wide environment key',
            status: 'info',
            duration: 5000,
            isClosable: true,
        });
        return updated;
    }, [fetchCredentials, toast]);

    useEffect(() => {
        fetchCredentials();
    }, [fetchCredentials]);

    return {
        credentials,
        isLoading,
        fetchCredentials,
        createCredential,
        rotateCredential,
        renameCredential,
        deleteCredential,
        allocateCredential,
        deallocateCredential,
    };
};
